import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen, ExternalLink, Calendar, User, Award,
  ChevronRight, Star, TrendingUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import sonYayinlarData from '../../data/SonYayinlar.json';

const PublicationsSection = () => {
  const { t } = useTranslation();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        // JSON dosyasından verileri yükle ve boş kayıtları filtrele
        const filteredData = sonYayinlarData.filter(pub => 
          pub && 
          pub.baslik && 
          pub.baslik.trim() !== '' && 
          pub.abstract && 
          pub.abstract.trim() !== '' &&
          pub.abstract.trim() !== '\n'
        );

        // JSON field'larını beklenen field'lara map et
        const mappedData = filteredData.map((pub, index) => ({
          id: index + 1,
          title: pub.baslik,
          authors: pub.yazarlar,
          publication_date: pub.tarih,
          publication_url: pub.link,
          image_url: pub.gorsel,
          abstract: pub.abstract,
          is_active: true
        }));

        // En yeni 6 yayını al ve tarihe göre sırala
        const sortedData = mappedData
          .sort((a, b) => {
            const dateA = parseDate(a.publication_date);
            const dateB = parseDate(b.publication_date);
            return dateB - dateA;
          })
          .slice(0, 6);
          
        setPublications(sortedData);
      } catch (err) {
        setError(err.message);
        setPublications([]);
      } finally {
        setLoading(false);
      }
    };

    const parseDate = (dateString) => {
      if (!dateString) return new Date();
      
      // Türkçe tarih formatını kontrol et
      if (dateString.includes('.')) {
        const [day, month, year] = dateString.split('.');
        if (day && month && year) {
          return new Date(year, month - 1, day);
        }
      }
      
      return new Date(dateString);
    };

    fetchPublications();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return t('home.publications.dateNotSpecified');
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long'
    });
  };

  const truncateTitle = (title, maxLength = 80) => {
    if (!title) return '';
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  const truncateAuthors = (authors, maxLength = 60) => {
    if (!authors) return t('home.publications.authorNotSpecified');
    return authors.length > maxLength ? authors.substring(0, maxLength) + '...' : authors;
  };

  const getCategoryColor = (index) => {
    const colors = [
      'from-blue-500 to-purple-600',
      'from-green-500 to-teal-600',
      'from-orange-500 to-red-600',
      'from-purple-500 to-pink-600',
      'from-teal-500 to-cyan-600',
      'from-red-500 to-orange-600'
    ];
    return colors[index % colors.length];
  };

  const handlePublicationClick = (publication) => {
    if (publication.publication_url) {
      window.open(publication.publication_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-12"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-80 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden">
      {/* Arka plan dekoratif elementler */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-purple-400/5 to-pink-400/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Başlık Bölümü */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center justify-center p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6">
            <div className="flex items-center space-x-2 bg-white rounded-full px-6 py-3">
              <BookOpen className="w-6 h-6 text-blue-600" />
              <span className="text-blue-600 font-semibold">{t('home.publications.badge')}</span>
            </div>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {t('home.publications.title')}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> {t('home.publications.titleHighlight')}</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('home.publications.subtitle')}
          </p>
        </motion.div>

        {/* Yayın Kartları */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {publications.map((publication, index) => (
            <motion.div
              key={publication.id}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 cursor-pointer"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              onClick={() => handlePublicationClick(publication)}
            >
              {/* Kategori Gradient Header */}
              <div className={`h-2 bg-gradient-to-r ${getCategoryColor(index)}`}></div>
              
              {/* Görsel varsa göster */}
              {publication.image_url && (
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={publication.image_url} 
                    alt={publication.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
              )}
              
              <div className="p-6">
                {/* Başlık */}
                <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight group-hover:text-blue-600 transition-colors duration-300 min-h-[3.5rem]">
                  {truncateTitle(publication.title)}
                </h3>

                {/* Yazarlar */}
                <div className="flex items-start space-x-3 mb-3">
                  <User className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                  <span className="text-gray-700 text-sm leading-relaxed">
                    {truncateAuthors(publication.authors)}
                  </span>
                </div>
                
                {/* Tarih */}
                <div className="flex items-center space-x-3 mb-4">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-500 text-sm">{formatDate(publication.publication_date)}</span>
                </div>

                {/* Özet varsa kısa bir kısmını göster */}
                {publication.abstract && (
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                    {publication.abstract.substring(0, 120)}...
                  </p>
                )}

                {/* Detay Linki */}
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-semibold text-sm group/link">
                    <span>{t('home.publications.viewPublication')}</span>
                    <ChevronRight className="w-4 h-4 transform group-hover/link:translate-x-1 transition-transform duration-200" />
                  </span>

                  {publication.publication_url && (
                    <div className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors duration-200">
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>

              {/* Hover Effect Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
            </motion.div>
          ))}
        </div>

        {/* Tümünü Görüntüle Butonu */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Link
            to="/yayinlar"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl"
          >
            <span>{t('home.publications.viewAllPublications')}</span>
            <ChevronRight className="w-5 h-5" />
          </Link>

          <p className="text-gray-600 mt-4 text-sm">
            {t('home.publications.exploreAll')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PublicationsSection; 